import * as React from 'react';
import { useState } from 'react';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import { IconButton } from '~shared/components/buttons/IconButton';
import { IconThumbsUp } from '~shared/components/icons/standard/IconThumbsUp/IconThumbsUp';
import { IconThumbsDown } from '~shared/components/icons/standard/IconThumbsDown/IconThumbsDown';

import { ActivityInsights } from '../../../../../api/activities/insights/insightSchema';
import { RateInsightsFeedbackDialog } from './RateInsightsFeedbackDialog';

type Rating = 'helpful' | 'not-helpful' | null;

interface RateInsightsProps {
  insights: ActivityInsights;
}

export function RateInsights({ insights }: RateInsightsProps) {
  const [selectedRating, setSelectedRating] = useState<Rating>(null);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  const labelHelpful = translate('Helpful');
  const labelNotHelpful = translate('Not helpful');

  const toggleRating = (rating: Exclude<Rating, null>) => {
    if (rating === 'not-helpful') {
      if (selectedRating === 'not-helpful') {
        setSelectedRating(null);
      } else {
        setIsFeedbackDialogOpen(true);
        setSelectedRating(rating);
      }
    } else {
      if (selectedRating === 'helpful') {
        setSelectedRating(null);
      } else {
        sendTeacherAmplitudeEvent(EventTypes.RateInsights, {
          insight_rating: 'helpful',
          insight_details: {
            insight_id: insights.id,
            insight_generation_number: insights.inputSettings.generationAttempt,
            ai_prompt_version: insights.inputSettings.promptVersion,
          },
        });
        setSelectedRating(rating);
      }
    }
  };

  const isHelpfulSelected = selectedRating === 'helpful';
  const isNotHelpfulSelected = selectedRating === 'not-helpful';

  const showThumbsUp = selectedRating === null || isHelpfulSelected;
  const showThumbsDown = selectedRating === null || isNotHelpfulSelected;

  return (
    <div>
      <span className="text-xs text-blue-900 font-400">
        {translate('Rate insights')}
      </span>

      <div className="flex items-center justify-center">
        {showThumbsUp && (
          <IconButton
            onClick={() => toggleRating('helpful')}
            aria-label={labelHelpful}
            aria-pressed={isHelpfulSelected}
            appearance="default"
          >
            <IconThumbsUp
              title={labelHelpful}
              titleId="rate-insights-helpful"
              className={isHelpfulSelected ? 'fill-current' : ''}
            />
          </IconButton>
        )}

        {showThumbsDown && (
          <IconButton
            onClick={() => toggleRating('not-helpful')}
            aria-label={labelNotHelpful}
            aria-pressed={isNotHelpfulSelected}
            appearance="default"
          >
            <IconThumbsDown
              title={labelNotHelpful}
              titleId="rate-insights-not-helpful"
              className={isNotHelpfulSelected ? 'fill-current' : ''}
            />
          </IconButton>
        )}
      </div>

      <RateInsightsFeedbackDialog
        open={isFeedbackDialogOpen}
        onClose={() => setIsFeedbackDialogOpen(false)}
        insights={insights}
      />
    </div>
  );
}
